import {
  TTS_DEFAULT_BULK_INSTRUCTION,
  TTS_DEFAULT_BULK_LANG_CODE,
  TTS_DEFAULT_LANG_CODE
} from 'consts/tts'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ttsThunks } from 'store/features/tts/thunks'
import { ttsSelectors } from 'store/features/tts/selectors'
import { voiceCatalogThunks } from 'store/features/voiceCatalog/thunks'
import { voiceCatalogSelectors } from 'store/features/voiceCatalog/selectors'
import { ITtsLanguageOption } from 'types/text-to-speech'
import {
  useGetCurrentSpeakerHasEmotion,
  useGetTtsSpeakers,
  usePremiumVoiceHandler
} from 'containers/SubSidebar/shared/Voices/hooks'
import { dispatch } from 'store'

export const useBulk = () => {
  const speakerTones = useSelector(voiceCatalogSelectors.speakerTones)
  const isTtsInitialItemsLoading = useSelector(
    ttsSelectors.isTtsInitialItemsLoading
  )
  const ttsLanguageOptions: ITtsLanguageOption[] | null = useSelector(
    voiceCatalogSelectors.languageOptionsData
  )
  const isLanguageOptionsLoading = useSelector(
    voiceCatalogSelectors.isLanguageOptionsLoading
  )
  const ttsBulkSpeed = useSelector(ttsSelectors.ttsBulkSpeedValue)
  const ttsBulkLanguageCode = useSelector(ttsSelectors.ttsBulkLangCodeValue)
  const ttsBulkSpeakerIdValue = useSelector(ttsSelectors.ttsBulkSpeakerIdValue)
  const ttsBulkToneInstructionValue = useSelector(
    ttsSelectors.ttsBulkToneInstructionValue
  )

  const isBulkLanguageUndefined = useMemo(
    () => ttsBulkLanguageCode === TTS_DEFAULT_BULK_LANG_CODE,
    [ttsBulkLanguageCode]
  )

  const currentLangCode = useMemo(() => {
    return isBulkLanguageUndefined ? TTS_DEFAULT_LANG_CODE : ttsBulkLanguageCode
  }, [ttsBulkLanguageCode, isBulkLanguageUndefined])

  const initialToneInstruction = useMemo(() => {
    return ttsBulkToneInstructionValue === TTS_DEFAULT_BULK_INSTRUCTION
      ? ''
      : ttsBulkToneInstructionValue
  }, [ttsBulkToneInstructionValue])

  const { ttsSpeakers, isSpeakersLoading } = useGetTtsSpeakers(currentLangCode)

  const hasSpeakerEmotion = useGetCurrentSpeakerHasEmotion(
    ttsBulkSpeakerIdValue,
    currentLangCode
  )
  const { handleVoiceSelection } = usePremiumVoiceHandler({
    speakers: ttsSpeakers || [],
    onVoiceSelect: (speakerId: number) => {
      dispatch(ttsThunks.setTtsBulkSpeakerId(speakerId))
    }
  })

  const onChooseSpeakerOption = useCallback(
    (currentSpeakerId: number) => {
      if (isBulkLanguageUndefined) {
        return
      }
      handleVoiceSelection(currentSpeakerId)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ttsBulkSpeakerIdValue, isBulkLanguageUndefined, ttsSpeakers]
  )

  const onChooseLanguageOption = useCallback(
    (langCode: string | number) => {
      if (langCode !== ttsBulkLanguageCode) {
        dispatch(ttsThunks.setTtsBulkLangCode(langCode as string))
      }
    },
    [ttsBulkLanguageCode]
  )

  const onChooseSpeedOption = useCallback(
    (value: number) => {
      if (value !== ttsBulkSpeed) {
        dispatch(ttsThunks.setTtsBulkSpeed(value))
      }
    },
    [ttsBulkSpeed]
  )

  const onToneInstructionChange = useCallback((instruction: string = '') => {
    dispatch(ttsThunks.setTtsBulkInstruction(instruction))
  }, [])

  useEffect(() => {
    if (!speakerTones) {
      dispatch(voiceCatalogThunks.getSpeakerTones())
    }
  }, [speakerTones])

  useEffect(() => {
    dispatch(voiceCatalogThunks.getLanguageOptions())
    dispatch(voiceCatalogThunks.getSpeakersPerLangCode(currentLangCode))
  }, [currentLangCode])

  return {
    isTtsInitialItemsLoading,
    ttsLanguageOptions,
    isLanguageOptionsLoading,
    ttsBulkSpeed,
    ttsBulkLanguageCode,
    ttsBulkSpeakerIdValue,
    ttsBulkToneInstructionValue,
    isBulkLanguageUndefined,
    currentLangCode,
    initialToneInstruction,
    ttsSpeakers,
    isSpeakersLoading,
    hasSpeakerEmotion,
    onChooseSpeakerOption,
    onChooseLanguageOption,
    onChooseSpeedOption,
    onToneInstructionChange
  }
}
